/**
 * Admin Roles & Permission Matrix — Phase 4 Redesign
 * Premium split layout: Role list | Permission matrix
 * Dirty-state handling, system role protections, Reset to Default
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import {
  getAdminRoles,
  getAdminPermissions,
  saveMatrixPermissions,
  resetRoleDefaults,
  createRole,
  deleteRole,
} from '../utils/rolesApi';
import PermissionMatrix from '../components/PermissionMatrix';
import RoleDrawer from '../components/RoleDrawer';
import { Button } from '../components/ui/button';
import {
  Plus, Download, Search, X, Shield, RotateCcw,
  Save, Undo2, Trash2, ChevronLeft,
} from 'lucide-react';
import './AdminRolesPage.css';

const AdminRolesPage = () => {
  const navigate = useNavigate();
  const { hasPermission, loading: permissionsLoading } = usePermissions();

  // Data
  const [roles, setRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);

  // Selection
  const [selectedRole, setSelectedRole] = useState(null);
  const [activePerms, setActivePerms] = useState(new Set());
  const [savedPerms, setSavedPerms] = useState(new Set());

  // UI state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showUnsavedConfirm, setShowUnsavedConfirm] = useState(null); // role to switch to
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState('create');

  const canView = hasPermission('rbac', 'view');
  const canUpdate = hasPermission('rbac', 'update');

  // Dirty state
  const isDirty = useMemo(() => {
    if (activePerms.size !== savedPerms.size) return true;
    for (const key of activePerms) {
      if (!savedPerms.has(key)) return true;
    }
    return false;
  }, [activePerms, savedPerms]);

  // Auto-clear messages
  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 3000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  useEffect(() => {
    if (error) {
      const t = setTimeout(() => setError(null), 6000);
      return () => clearTimeout(t);
    }
  }, [error]);

  // Fetch data
  useEffect(() => {
    if (!permissionsLoading && canView) {
      fetchData();
    } else if (!permissionsLoading && !canView) {
      setLoading(false);
    }
  }, [permissionsLoading, canView]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [rolesData, permsData] = await Promise.all([
        getAdminRoles(),
        getAdminPermissions()
      ]);
      setRoles(rolesData);
      setAllPermissions(permsData);

      // Select first role if none selected
      if (rolesData.length > 0 && !selectedRole) {
        selectRole(rolesData[0]);
      } else if (selectedRole) {
        // Re-select current role with fresh data
        const updated = rolesData.find(r => r.id === selectedRole.id);
        if (updated) selectRole(updated);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const selectRole = useCallback((role) => {
    const perms = new Set(
      (role.permissions || []).map(p => `${p.resource}:${p.action}`)
    );
    setSelectedRole(role);
    setActivePerms(new Set(perms));
    setSavedPerms(new Set(perms));
  }, []);

  // Role selection with dirty guard
  const handleSelectRole = (role) => {
    if (role.id === selectedRole?.id) return;
    if (isDirty) {
      setShowUnsavedConfirm(role);
      return;
    }
    selectRole(role);
  };

  const confirmSwitchRole = () => {
    if (showUnsavedConfirm) {
      selectRole(showUnsavedConfirm);
      setShowUnsavedConfirm(null);
    }
  };

  // Permission toggles
  const handleToggle = useCallback((resource, action) => {
    setActivePerms(prev => {
      const next = new Set(prev);
      const key = `${resource}:${action}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const handleToggleGroup = useCallback((resource, selectAll) => {
    setActivePerms(prev => {
      const next = new Set(prev);
      const groupPerms = allPermissions.filter(p => p.resource === resource);
      for (const perm of groupPerms) {
        const key = `${perm.resource}:${perm.action}`;
        if (selectAll) next.add(key);
        else next.delete(key);
      }
      return next;
    });
  }, [allPermissions]);

  // Save
  const handleSave = async () => {
    if (!selectedRole || !canUpdate) return;
    try {
      setSaving(true);
      setError(null);
      const grants = Array.from(activePerms).map(key => {
        const [resource, action] = key.split(':');
        return { role: selectedRole.name, resource, action };
      });

      if (grants.length === 0) {
        setError('Cannot save with zero permissions. At least one permission must remain active.');
        return;
      }

      await saveMatrixPermissions(grants);

      // Refresh
      const rolesData = await getAdminRoles();
      setRoles(rolesData);
      const updated = rolesData.find(r => r.id === selectedRole.id);
      if (updated) selectRole(updated);
      setSuccessMsg('Permissions saved successfully');
    } catch (err) {
      setError('Save failed: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  // Discard
  const handleDiscard = () => {
    setActivePerms(new Set(savedPerms));
  };

  // Reset to defaults
  const handleResetDefaults = async () => {
    if (!selectedRole) return;
    try {
      setResetting(true);
      setError(null);
      await resetRoleDefaults(selectedRole.name);

      const rolesData = await getAdminRoles();
      setRoles(rolesData);
      const updated = rolesData.find(r => r.id === selectedRole.id);
      if (updated) selectRole(updated);
      setShowResetConfirm(false);
      setSuccessMsg(`"${selectedRole.name}" reset to default permissions`);
    } catch (err) {
      setError('Reset failed: ' + err.message);
    } finally {
      setResetting(false);
    }
  };

  // Delete role
  const handleDeleteRole = async (role) => {
    if (role.is_system_role) return;
    if (!window.confirm(`Delete role "${role.name}"? This cannot be undone.`)) return;
    try {
      await deleteRole(role.id);
      const rolesData = await getAdminRoles();
      setRoles(rolesData);
      if (selectedRole?.id === role.id && rolesData.length > 0) {
        selectRole(rolesData[0]);
      }
      setSuccessMsg(`Role "${role.name}" deleted`);
    } catch (err) {
      setError('Delete failed: ' + err.message);
    }
  };

  // Create role via drawer
  const handleCreateRole = () => {
    setDrawerMode('create');
    setDrawerOpen(true);
  };

  const handleSaveRole = async (roleData) => {
    try {
      await createRole(roleData);
      await fetchData();
    } catch (err) {
      throw new Error('Failed to create role: ' + err.message);
    }
  };

  // Export CSV
  const handleExportCSV = () => {
    const headers = ['Role', 'Type', ...allPermissions.map(p => `${p.resource}:${p.action}`)];
    const rows = roles.map(role => {
      const permSet = new Set((role.permissions || []).map(p => `${p.resource}:${p.action}`));
      return [
        role.name,
        role.is_system_role ? 'System' : 'Custom',
        ...allPermissions.map(p => permSet.has(`${p.resource}:${p.action}`) ? 'Y' : '')
      ];
    });
    const csv = [headers.join(','), ...rows.map(r => r.map(c => `"${c}"`).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `rbac_matrix_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  // Filter roles
  const filteredRoles = roles.filter(role =>
    role.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (role.description || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Loading
  if (permissionsLoading || (loading && roles.length === 0)) {
    return (
      <div className="ar-page" data-testid="admin-roles-page">
        <div className="ar-loading">
          <div className="ar-spinner" />
          <p>Loading roles and permissions...</p>
        </div>
      </div>
    );
  }

  // Access denied
  if (!canView) {
    return (
      <div className="ar-page" data-testid="admin-roles-page">
        <div className="ar-denied">
          <Shield size={48} />
          <h2>Access Denied</h2>
          <p>You don't have permission to view the RBAC matrix.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ar-page" data-testid="admin-roles-page">
      {/* Breadcrumb */}
      <div className="ar-breadcrumb">
        <button
          className="ar-breadcrumb-link"
          onClick={() => navigate('/dashboard')}
          data-testid="breadcrumb-dashboard"
        >
          <ChevronLeft size={14} /> Dashboard
        </button>
        <span className="ar-breadcrumb-sep">/</span>
        <span className="ar-breadcrumb-current">Roles & Permission Matrix</span>
      </div>

      {/* Header */}
      <div className="ar-header">
        <div>
          <h1 data-testid="page-title">Roles & Permission Matrix</h1>
          <p className="ar-subtitle">Manage role-based access controls for your organization</p>
        </div>
        <div className="ar-header-actions">
          <Button variant="outline" onClick={handleExportCSV} data-testid="export-csv-btn">
            <Download size={15} /> Export CSV
          </Button>
          {canUpdate && (
            <Button onClick={handleCreateRole} data-testid="create-role-btn">
              <Plus size={15} /> New Role
            </Button>
          )}
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="ar-msg ar-msg-error" data-testid="error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={14} /></button>
        </div>
      )}
      {successMsg && (
        <div className="ar-msg ar-msg-success" data-testid="success-message">
          <span>{successMsg}</span>
        </div>
      )}

      {/* Main content */}
      {loading ? (
        <div className="ar-loading"><div className="ar-spinner" /></div>
      ) : (
        <div className="ar-content">
          {/* Left panel — Role list */}
          <div className="ar-sidebar" data-testid="roles-sidebar">
            <div className="ar-sidebar-header">
              <h3>Roles <span className="ar-role-count">{filteredRoles.length}</span></h3>
              <div className="ar-search-wrap">
                <Search size={14} className="ar-search-icon" />
                <input
                  type="text"
                  placeholder="Search roles..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="ar-search-input"
                  data-testid="role-search-input"
                />
                {searchQuery && (
                  <button
                    className="ar-search-clear"
                    onClick={() => setSearchQuery('')}
                    data-testid="role-search-clear"
                  >
                    <X size={12} />
                  </button>
                )}
              </div>
            </div>

            <div className="ar-role-list" data-testid="role-list">
              {filteredRoles.map(role => {
                const isSelected = selectedRole?.id === role.id;
                const permCount = (role.permissions || []).length;

                return (
                  <div
                    key={role.id}
                    className={`ar-role-card ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleSelectRole(role)}
                    data-testid={`role-card-${role.id}`}
                  >
                    {isSelected && <div className="ar-role-indicator" />}
                    <div className="ar-role-card-body">
                      <div className="ar-role-card-top">
                        <span className="ar-role-name">{role.name}</span>
                        {role.is_system_role && (
                          <span className="ar-badge-system" data-testid={`badge-system-${role.id}`}>
                            System
                          </span>
                        )}
                      </div>
                      <p className="ar-role-desc">
                        {role.description || 'No description'}
                      </p>
                      <div className="ar-role-meta">
                        <span className="ar-role-meta-item">
                          <Shield size={11} /> {permCount} permission{permCount !== 1 ? 's' : ''}
                        </span>
                      </div>
                    </div>
                    {canUpdate && !role.is_system_role && (
                      <button
                        className="ar-role-delete"
                        onClick={e => { e.stopPropagation(); handleDeleteRole(role); }}
                        title="Delete role"
                        data-testid={`delete-role-${role.id}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                );
              })}
              {filteredRoles.length === 0 && (
                <div className="ar-empty-list">
                  <p>No roles match your search</p>
                </div>
              )}
            </div>
          </div>

          {/* Right panel — Role detail + Matrix */}
          <div className="ar-detail" data-testid="role-detail-panel">
            {selectedRole ? (
              <>
                {/* Detail header */}
                <div className="ar-detail-header">
                  <div className="ar-detail-info">
                    <div className="ar-detail-title-row">
                      <h2 data-testid="selected-role-name">{selectedRole.name}</h2>
                      {selectedRole.is_system_role && (
                        <span className="ar-badge-system-lg">System Role</span>
                      )}
                    </div>
                    {selectedRole.description && (
                      <p className="ar-detail-desc">{selectedRole.description}</p>
                    )}
                  </div>

                  <div className="ar-detail-actions">
                    {/* Dirty state actions */}
                    {isDirty && canUpdate && (
                      <>
                        <Button
                          variant="outline"
                          onClick={handleDiscard}
                          data-testid="discard-btn"
                        >
                          <Undo2 size={14} /> Discard
                        </Button>
                        <Button
                          onClick={handleSave}
                          isLoading={saving}
                          data-testid="save-btn"
                          className="ar-save-btn"
                        >
                          <Save size={14} /> Save Changes
                        </Button>
                      </>
                    )}
                    {/* Reset to Default — system roles only */}
                    {selectedRole.is_system_role && canUpdate && !isDirty && (
                      <Button
                        variant="outline"
                        onClick={() => setShowResetConfirm(true)}
                        data-testid="reset-defaults-btn"
                        className="ar-reset-btn"
                      >
                        <RotateCcw size={14} /> Reset to Default
                      </Button>
                    )}
                  </div>
                </div>

                {/* Dirty state indicator */}
                {isDirty && (
                  <div className="ar-dirty-banner" data-testid="unsaved-changes-banner">
                    You have unsaved changes
                  </div>
                )}

                {/* Permission Matrix */}
                <PermissionMatrix
                  allPermissions={allPermissions}
                  activePerms={activePerms}
                  onToggle={handleToggle}
                  onToggleGroup={handleToggleGroup}
                  readOnly={!canUpdate}
                />
              </>
            ) : (
              <div className="ar-empty-detail" data-testid="no-role-selected">
                <Shield size={40} />
                <p>Select a role to view and edit permissions</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Unsaved changes confirmation modal */}
      {showUnsavedConfirm && (
        <div className="ar-modal-overlay" data-testid="unsaved-confirm-modal">
          <div className="ar-modal">
            <h3>Unsaved Changes</h3>
            <p>
              You have unsaved changes to <strong>{selectedRole?.name}</strong>.
              Switching roles will discard them.
            </p>
            <div className="ar-modal-actions">
              <Button
                variant="outline"
                onClick={() => setShowUnsavedConfirm(null)}
                data-testid="unsaved-cancel-btn"
              >
                Stay
              </Button>
              <Button
                variant="destructive"
                onClick={confirmSwitchRole}
                data-testid="unsaved-discard-btn"
              >
                Discard & Switch
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Reset to Default confirmation modal */}
      {showResetConfirm && (
        <div className="ar-modal-overlay" data-testid="reset-confirm-modal">
          <div className="ar-modal">
            <div className="ar-modal-icon-wrap">
              <RotateCcw size={24} />
            </div>
            <h3>Reset to Default Permissions?</h3>
            <p>
              This will replace all current permissions for <strong>{selectedRole?.name}</strong> with
              the original system defaults. This action cannot be undone.
            </p>
            <div className="ar-modal-actions">
              <Button
                variant="outline"
                onClick={() => setShowResetConfirm(false)}
                data-testid="reset-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                onClick={handleResetDefaults}
                isLoading={resetting}
                data-testid="reset-confirm-btn"
              >
                Reset Permissions
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Role Drawer */}
      <RoleDrawer
        role={selectedRole}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        onSave={handleSaveRole}
        mode={drawerMode}
      />
    </div>
  );
};

export default AdminRolesPage;
